import { Suggestion } from '@prophecy/ui/Editor';
import { PrepareInsertText, PrepareSuggestions } from '@prophecy/ui/Editor/types';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { createContext, useContext, useMemo } from 'react';

import { BackEndCodeLanguage, BackEndCodeLanguageType } from '../graphqlTypes/enums';

export type CodeSuggestions = Partial<Record<`${BackEndCodeLanguage}`, Suggestion[]>>;

export enum SuggestionKind {
  configuration = 'configuration',
  function = 'function',
  column = 'column'
}

const defaultSuggestions = Object.fromEntries(
  Object.values(BackEndCodeLanguage).map((language) => [language, [] as Suggestion[]])
) as CodeSuggestions;

export const CodeSuggestionContext = createContext<{
  suggestions: CodeSuggestions;
  prepareSuggestions: PrepareSuggestions;
  prepareInsertText: PrepareInsertText;
}>({
  suggestions: defaultSuggestions,
  prepareSuggestions: ({ suggestions }) => suggestions,
  prepareInsertText: ({ insertText }) => insertText
});

export function CodeSuggestionProvider({
  children,
  suggestions,
  prepareSuggestions = ({ suggestions }) => suggestions,
  prepareInsertText = ({ insertText }) => insertText
}: {
  children: React.ReactNode;
  suggestions: CodeSuggestions;
  prepareSuggestions?: PrepareSuggestions;
  prepareInsertText?: PrepareInsertText;
}) {
  const _prepareSuggestions = usePersistentCallback(prepareSuggestions);
  const _prepareInsertText = usePersistentCallback(prepareInsertText);
  const value = useMemo(
    () => ({ suggestions, prepareSuggestions: _prepareSuggestions, prepareInsertText: _prepareInsertText }),
    [suggestions, _prepareSuggestions, _prepareInsertText]
  );

  return <CodeSuggestionContext.Provider value={value}>{children}</CodeSuggestionContext.Provider>;
}

export function useCodeSuggestions(language: BackEndCodeLanguageType) {
  const { suggestions, prepareSuggestions, prepareInsertText } = useContext(CodeSuggestionContext);

  return {
    suggestions: suggestions[language] || [],
    prepareSuggestions,
    prepareInsertText
  };
}
